import React, { lazy, Suspense, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import Login from './Login';
import Register from './Register';
import LoadingFallback from './LoadingFallback';

const SharedGalleryPage = lazy(() => import('../pages/SharedGalleryPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));

/** Shared galleries are public: a hashid link works without an account. */
function SharedGalleryRoute() {
    const { hashid } = useParams();
    return <SharedGalleryPage hashid={hashid} isAuthenticated={false} />;
}

function ResetPasswordRoute() {
    const { uid, token } = useParams();
    const navigate = useNavigate();
    return <ResetPasswordPage uid={uid} token={token} onBackToLogin={() => navigate('/', { replace: true })} />;
}

/**
 * PublicRoutes — what a signed-out visitor can reach.
 *
 * Only shared galleries and the password-reset link are real destinations;
 * every other path shows the login (or registration) form, so a deep link
 * survives the sign-in and is reachable again afterwards.
 *
 * @param {string|null} notice message shown above the login form, e.g. when the
 *   session expired mid-visit.
 */
function PublicRoutes({ onLoginSuccess, onRegisterSuccess, notice = null }) {
    const [showLogin, setShowLogin] = useState(true);

    const authForm = showLogin ? (
        <Login onLoginSuccess={onLoginSuccess} onSwitchToRegister={() => setShowLogin(false)} notice={notice} />
    ) : (
        <Register onRegisterSuccess={onRegisterSuccess} onSwitchToLogin={() => setShowLogin(true)} />
    );

    return (
        <Suspense fallback={<LoadingFallback />}>
            <Routes>
                <Route path="/g/:hashid" element={<SharedGalleryRoute />} />
                <Route path="/reset-password/:uid/:token" element={<ResetPasswordRoute />} />
                <Route path="*" element={authForm} />
            </Routes>
        </Suspense>
    );
}

export default PublicRoutes;
